import { createContext, useContext, useEffect, useState } from "react";
import axiosClient from "../utils/axios";

const AuthContext = createContext<AuthProps>(null);

export const useAuth = (): AuthProps => {
  return useContext(AuthContext);
};

type LoginStatus = "LOGGED_IN" | "LOGGED_OUT" | "UNKNOWN";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState<User>(null);
  const [token, setToken] = useState<string>(null);
  const [loginStatus, setLoginStatus] = useState<LoginStatus>("UNKNOWN");
  const [blockedUserIds, setBlockedUserIds] = useState<string[]>(() => {
    if (typeof window !== "undefined") {
      try {
        const stored = sessionStorage.getItem("blocked_user_ids");
        if (stored) return JSON.parse(stored);
      } catch (_) {}
    }
    return [];
  });

  const storeBlockedUsers = (ids: string[]) => {
    try {
      sessionStorage.setItem("blocked_user_ids", JSON.stringify(ids || []));
    } catch (_) {}
    setBlockedUserIds(ids || []);
  };

  // Exposed method to allow pages/components to update blocked user ids after block/unblock API calls
  const updateBlockedUsers = (ids: string[]) => {
    storeBlockedUsers(ids || []);
  };

  const fetchBlockedUsers = async () => {
    try {
      const res = await axiosClient.get("/admin/blocked-users");
      console.log("Blocked users fetched:", res.data);
      if (res?.status === 200 && Array.isArray(res.data?.blockedUserIds)) {
        storeBlockedUsers(res.data.blockedUserIds);
      } else {
        storeBlockedUsers([]);
      }
    } catch (e) {
      // Swallow errors silently; absence of list means no blocked users loaded
      storeBlockedUsers([]);
    }
  };

  const login = async (email: string, password: string) => {
    const res = await axiosClient.post("/auth/admin/login", {
      userName: email,
      password
    });

    if (res.status === 200) {
      sessionStorage.setItem("am_token", res.data.token);

      sessionStorage.setItem("am_user", JSON.stringify(res.data.user));

      setToken(res.data.token);
      setUser(res.data.user);
      setLoginStatus("LOGGED_IN");

      // If superadmin, fetch blocked users list
      if (res.data?.user?.permissions?.superadmin === true) {
        fetchBlockedUsers();
      } else {
        // Clear any previously stored blocked users if logging in as non superadmin
        storeBlockedUsers([]);
      }
    }

    const response = await axiosClient.get("/admin/me");
    // console.log(response);

    return true;
  };

  const sendFCMToken = async (token = null) => {
    await axiosClient.post("/auth/fcm-token", {
      fcmToken: token || sessionStorage.getItem("fcm_token")
    });
  };

  const updateUserPermissions = (newPermissions: any) => {
    const currentUser =
      user || JSON.parse(sessionStorage.getItem("am_user") || "{}");

    const updatedUser = {
      ...currentUser,
      permissions: newPermissions
    };

    // Update session storage
    sessionStorage.setItem("am_user", JSON.stringify(updatedUser));

    // Update React state to trigger re-renders
    setUser(updatedUser);
  };

  const getPermissions = () => {
    const currentUser = user || JSON.parse(sessionStorage.getItem("am_user"));
    if (!currentUser || !currentUser.permissions) return [];

    console.log(currentUser.permissions);
    let perms = Object.keys(currentUser.permissions);
    return perms;
  };

  const hasPermission = (
    module: string,
    action: "read" | "write" | "update" | "delete"
  ) => {
    const currentUser = user || JSON.parse(sessionStorage.getItem("am_user"));
    if (!currentUser || !currentUser.permissions) return false;

    // Check if user is superadmin
    if (
      currentUser.permissions.superadmin === true ||
      currentUser.email === "tech@advancemobility.io"
    )
      return true;

    // Check module-specific permission
    const modulePermissions = currentUser.permissions[module];
    if (!modulePermissions) return false;

    return modulePermissions[action] === true;
  };

  const hasView = (module: string, view: string) => {
    const currentUser = user || JSON.parse(sessionStorage.getItem("am_user"));
    if (!currentUser || !currentUser.permissions) return false;

    // Check if user is superadmin
    if (
      currentUser.permissions.superadmin === true ||
      currentUser.email === "tech@advancemobility.io"
    )
      return true;

    // Check module-specific view permission
    const modulePermissions = currentUser.permissions[module];
    if (!modulePermissions || !modulePermissions.views) return false;

    return modulePermissions.views.includes(view);
  };

  const isSuperAdmin = () => {
    const currentUser = user || JSON.parse(sessionStorage.getItem("am_user"));
    if (!currentUser || !currentUser.permissions) return false;

    return currentUser.permissions.superadmin === true || currentUser.id === "AMA000006";
  };

  const checkkPermissions = (permission: string) => {
    const currentUser = user || JSON.parse(sessionStorage.getItem("am_user"));
    if (currentUser && currentUser.permissions) {
      return currentUser.permissions[permission] || false;
    }

    return false;
  };

  const logout = () => {
    sessionStorage.removeItem("am_token");
    sessionStorage.removeItem("am_user");
    sessionStorage.removeItem("blocked_user_ids");
    setToken(null);
    setUser(null);
    setLoginStatus("LOGGED_OUT");
    setBlockedUserIds([]);
  };

  useEffect(() => {
    if (sessionStorage.getItem("am_token") != null) {
      setToken(sessionStorage.getItem("am_token"));
    }

    if (sessionStorage.getItem("am_user") != null) {
      setUser(JSON.parse(sessionStorage.getItem("am_user")));
      setLoginStatus("LOGGED_IN");
      // On refresh, if superadmin and we don't yet have blocked users in state but do in storage, load them
      const stored = sessionStorage.getItem("blocked_user_ids");
      if (stored && blockedUserIds.length === 0) {
        try {
          setBlockedUserIds(JSON.parse(stored));
        } catch (_) {}
      }
    } else {
      setLoginStatus("LOGGED_OUT");
    }
  }, []);

  const isBlockedUser = (id: string): boolean => {
    if (!id) return false;
    const list = blockedUserIds.length
      ? blockedUserIds
      : (() => {
          try {
            const stored = sessionStorage.getItem("blocked_user_ids");
            return stored ? JSON.parse(stored) : [];
          } catch (_) {
            return [];
          }
        })();
    return list.includes(id);
  };

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        user,
        token,
        loginStatus,
        getPermissions,
        hasPermission,
        hasView,
        isSuperAdmin,
        updateUserPermissions,
        isBlockedUser,
        blockedUserIds,
        updateBlockedUsers
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

interface AuthProps {
  login: (email: string, password: string) => Promise<any>;
  logout: () => void;
  user: User;
  token: string;
  loginStatus: LoginStatus;
  getPermissions: () => string[];
  hasPermission: (
    module: string,
    action: "read" | "write" | "update" | "delete"
  ) => boolean;
  hasView: (module: string, view: string) => boolean;
  isSuperAdmin: () => boolean;
  updateUserPermissions: (newPermissions: any) => void;
  isBlockedUser: (id: string) => boolean;
  blockedUserIds: string[];
  updateBlockedUsers: (ids: string[]) => void;
}

interface User {
  id: string;
  name: string;
  email: string;
  type: string;
  permissions?: {
    superadmin?: boolean;
    [module: string]:
      | {
          read: boolean;
          write: boolean;
          update: boolean;
          delete: boolean;
          views: string[];
        }
      | boolean;
  };
}
